/**
 * State snapshot and revert for the approve/revert flow.
 * Lives in the frontend app (not the edge-ai package) so the package
 * cannot directly mutate workspace state.
 */

import type { ToolCall } from '@edge/edge-ai'
import { revertAiDrawingBatch } from '@/features/charts/companion/ai-drawing-batches'
import { resolveChartWriteTarget } from '@/features/charts/companion/chart-target'
import type { ActionCallbacks, ActionSnapshot } from './types'
import { resolveColumnTarget, resolveFilterTarget } from './panel-capabilities'
import { revertSavedStrategyEdit } from './saved-strategy-edits'

let snapshotCounter = 0

/**
 * Capture a snapshot of the state that will be affected by the given tool calls.
 * Only captures the slices that the tools will mutate — not the entire workspace.
 */
export function takeSnapshot(toolCalls: ToolCall[], callbacks: ActionCallbacks): ActionSnapshot {
  const snapshot: ActionSnapshot = {
    id: `snap_${++snapshotCounter}_${Date.now()}`,
    timestamp: Date.now(),
    description: '',
    panelSnapshots: {},
  }

  const descriptions: string[] = []
  const affectedPanelIds = new Set<string>()
  const periodKeys = new Set<string>()
  // Panels a filter, column or news-source call writes keys into.
  const writtenPanelIds = new Set<string>()
  const written = (panelId: string) => {
    affectedPanelIds.add(panelId)
    writtenPanelIds.add(panelId)
  }
  let needsLayout = false
  let needsGlobalSettings = false
  const aiDrawingCalls: string[] = []
  const savedStrategyEditCalls: string[] = []

  for (const call of toolCalls) {
    switch (call.name) {
      case 'add_panel':
        needsLayout = true
        descriptions.push(`Add ${(call.input.component_type as string) || 'panel'}`)
        break

      // Both write the layout's windowConfigs (symbolIndex, linkMode).
      case 'set_ticker':
        needsLayout = true
        descriptions.push('Set ticker')
        break

      case 'set_link_mode':
        needsLayout = true
        descriptions.push('Set link mode')
        break

      // The key the tool writes: in a grid, a cell (or the panel's timeframe
      // under interval sync), the same target the tool resolves.
      case 'set_timeframe':
      case 'configure_indicator':
      case 'remove_indicator': {
        const target = resolveChartWriteTarget(callbacks, call.input.panel_id)
        if (target) {
          const key = call.name === 'set_timeframe' ? target.periodId : target.cellId
          affectedPanelIds.add(key)
          if (call.name === 'set_timeframe') periodKeys.add(key)
        }
        descriptions.push(
          call.name === 'set_timeframe'
            ? 'Set timeframe'
            : call.name === 'remove_indicator'
              ? 'Remove indicator'
              : 'Configure indicator',
        )
        break
      }

      case 'draw_on_chart':
      case 'draw_from_script':
      case 'clear_ai_drawings':
        aiDrawingCalls.push(call.tool_use_id)
        descriptions.push(call.name === 'clear_ai_drawings' ? 'Clear AI drawings' : 'Draw on chart')
        break

      // The target the tool resolves (panel-capabilities.ts): a panel's state,
      // or the History page's page-wide filters in globalSettings, whose whole
      // blob is snapshotted so revert restores it verbatim. set_history_filters,
      // the old name, always sets the page's.
      case 'set_filters':
      case 'set_history_filters':
      case 'clear_filters': {
        const target = resolveFilterTarget(callbacks, call.input.panel_id, {
          clear: call.name === 'clear_filters',
          page: call.name === 'set_history_filters',
        })
        const page = 'kind' in target && target.kind === 'page' ? target.id : null
        if (call.name === 'set_history_filters' || page === 'history') needsGlobalSettings = true
        else if (page && callbacks.pageFilters?.page === page)
          snapshot.pageFiltersSnapshot = {
            page,
            state: structuredClone(callbacks.pageFilters.read()),
          }
        else if (!('error' in target)) written(target.id)
        descriptions.push(
          call.name === 'clear_filters'
            ? 'Clear filters'
            : call.name === 'set_history_filters'
              ? 'Set history filters'
              : 'Set filters',
        )
        break
      }

      // The tools that exist (the old `update_columns` case matched nothing,
      // so Undo did nothing for a column edit). Same target as the tool.
      case 'add_columns':
      case 'remove_columns':
      case 'set_columns': {
        const target = resolveColumnTarget(callbacks, call.input.panel_id)
        if (!('error' in target)) written(target.id)
        descriptions.push('Update columns')
        break
      }

      case 'set_news_sources': {
        const panelId =
          (call.input.panel_id as string | undefined) ??
          callbacks.getPanelsByType?.('news')?.[0]?.panelId
        if (panelId) written(panelId)
        descriptions.push('Set news sources')
        break
      }

      // The layout loses the leaf and the store drops the panel's state, so
      // Undo needs both.
      case 'delete_panel': {
        needsLayout = true
        const panelId = call.input.panel_id as string | undefined
        if (panelId) affectedPanelIds.add(panelId)
        descriptions.push('Delete panel')
        break
      }

      // Undo writes back the graph the edit replaced, recorded once it ran.
      case 'edit_saved_strategy':
        savedStrategyEditCalls.push(call.tool_use_id)
        descriptions.push('Change a saved strategy')
        break

      case 'generate_history_layout':
        needsLayout = true
        descriptions.push('Generate history layout')
        break
    }
  }

  for (const panelId of affectedPanelIds) {
    const state = callbacks.getPanel?.(panelId)
    // A cell showing the panel's timeframe has none of its own: Undo writes
    // `period: undefined` back, so the cell follows the panel again. A panel
    // with nothing stored gets an empty entry too, so the keys a call adds come
    // off on Undo (recordingWrites).
    const clone = state
      ? structuredClone(state)
      : periodKeys.has(panelId) || writtenPanelIds.has(panelId)
        ? {}
        : null
    if (!clone) continue
    if (periodKeys.has(panelId) && !('period' in clone)) clone.period = undefined
    snapshot.panelSnapshots[panelId] = clone
  }

  if (needsLayout && callbacks.getLayout) {
    snapshot.layoutSnapshot = structuredClone(callbacks.getLayout())
  }

  if (needsGlobalSettings && callbacks.getGlobalSettings) {
    snapshot.globalSettingsSnapshot = structuredClone(callbacks.getGlobalSettings())
  }

  if (aiDrawingCalls.length) snapshot.aiDrawingCalls = aiDrawingCalls
  if (savedStrategyEditCalls.length) snapshot.savedStrategyEditCalls = savedStrategyEditCalls

  snapshot.description = descriptions.join(', ')
  return snapshot
}

/**
 * `callbacks` whose panel writes note, in `snapshot`, each key a panel it covers
 * did not have. Undo writes those back as undefined, so the panel shows what it
 * did before (its profile, its default columns) rather than what the AI wrote:
 * the layout store merges a write into a panel's state, so a key the snapshot
 * lacks would survive Undo (2026-10-18, a Scanner and a News panel still on
 * their profiles). The chart cell's `period: undefined` is the same idea.
 */
export function recordingWrites(
  callbacks: ActionCallbacks,
  snapshot: ActionSnapshot,
): ActionCallbacks {
  const update = callbacks.updatePanel
  if (!update) return callbacks
  return {
    ...callbacks,
    updatePanel: (panelId, partial) => {
      const before = snapshot.panelSnapshots[panelId]
      if (before)
        for (const key of Object.keys(partial)) if (!(key in before)) before[key] = undefined
      update(panelId, partial)
    },
  }
}

/**
 * Revert workspace state to a previous snapshot.
 */
export function revertSnapshot(snapshot: ActionSnapshot, callbacks: ActionCallbacks): void {
  // Layout first, so a deleted panel is back in the layout before its state is.
  if (snapshot.layoutSnapshot) {
    callbacks.updateLayout?.(
      snapshot.layoutSnapshot.mosaicTree,
      snapshot.layoutSnapshot.windowConfigs,
    )
  }

  for (const [panelId, state] of Object.entries(snapshot.panelSnapshots)) {
    callbacks.updatePanel?.(panelId, state)
  }

  if (snapshot.globalSettingsSnapshot) {
    callbacks.setGlobalSettings?.(snapshot.globalSettingsSnapshot)
  }

  const pageFilters = snapshot.pageFiltersSnapshot
  if (pageFilters && callbacks.pageFilters?.page === pageFilters.page) {
    callbacks.pageFilters.restore(pageFilters.state)
  }

  // Latest call first, so a draw then a clear in one turn unwinds in order.
  for (const id of [...(snapshot.aiDrawingCalls ?? [])].reverse()) revertAiDrawingBatch(id)
  for (const id of [...(snapshot.savedStrategyEditCalls ?? [])].reverse())
    revertSavedStrategyEdit(id)
}

/** "scanner" for a panel id, from its config; "panel" when unknown. */
function panelKind(callbacks: ActionCallbacks, panelId: unknown): string {
  if (typeof panelId !== 'string') return 'panel'
  const config = callbacks.getLayout?.()?.windowConfigs?.[panelId] as
    | { componentType?: string }
    | undefined
  return config?.componentType ?? 'panel'
}

/** graph_call methods that ask first, in the trader's words. */
const GRAPH_METHOD_LINES: Record<string, string> = {
  saveStrategy: 'Save the strategy',
  renameStrategy: 'Rename the strategy',
  newStrategy: 'Start a new strategy',
  loadStrategy: 'Open another strategy',
  duplicateStrategy: 'Duplicate the strategy',
  setAutoRun: 'Change auto-run',
  run: 'Run the backtest',
}

/**
 * One line per call, in the trader's words, for the confirm card (2026-10-15).
 * Covers the calls that ask first; anything else falls back to its name.
 */
export function describeToolCall(call: ToolCall, callbacks: ActionCallbacks): string {
  const input = call.input ?? {}
  switch (call.name) {
    case 'delete_panel':
      return `Delete the ${panelKind(callbacks, input.panel_id)} panel`
    case 'clear_filters': {
      const target = resolveFilterTarget(callbacks, input.panel_id, { clear: true })
      const kind =
        'kind' in target && target.kind === 'panel'
          ? target.type
          : panelKind(callbacks, input.panel_id)
      return `Clear every filter on the ${kind} panel`
    }
    // Chart tools act only on charts, and their calls usually omit panel_id.
    case 'clear_ai_drawings':
      return 'Remove the AI drawings from the chart'
    case 'remove_indicator':
      return `Remove ${String(input.indicator ?? 'an indicator').toUpperCase()} from the chart`
    case 'generate_history_layout':
      return typeof input.prompt === 'string' && input.prompt
        ? `Replace the History layout with one built for "${input.prompt}"`
        : 'Replace the History layout'
    case 'create_saved_strategy':
      return typeof input.name === 'string' && input.name
        ? `Save a new strategy, "${input.name}" (it cannot be undone here)`
        : 'Save a new strategy (it cannot be undone here)'
    case 'edit_saved_strategy':
      return 'Change a saved strategy'
    case 'run_saved_strategy':
      return typeof input.ticker === 'string' && typeof input.date === 'string'
        ? `Run a saved strategy on ${input.ticker} for ${input.date}`
        : 'Run a saved strategy'
    case 'rerun_backtest':
      return 'Run the backtest again'
    case 'open_script_on_chart':
      return input.kind === 'strategy'
        ? 'Open the strategy on a chart'
        : 'Open the indicator on a chart'
    case 'graph_call': {
      const method = typeof input.method === 'string' ? input.method : ''
      return GRAPH_METHOD_LINES[method] ?? 'Change the strategy'
    }
  }
  const words = call.name.replace(/_/g, ' ')
  return words.charAt(0).toUpperCase() + words.slice(1)
}
